import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Surface } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { HeaderWithBackNavigation } from '@app/components/HeaderWithBackNavigation';
import { useScreenResolution } from '@app/hooks';
import { refetchPlans, resetHideBroadbandLabels, selectChangePlan, toggleBroadbandLabels } from '@app/redux/changePlan';
import { selectConfig } from '@app/redux/config';
import { selectCurrentPlanPrice, selectPlanCharacteristics } from '@app/redux/plan';

import { Platform } from '@mfe/shared/schema-types';

import AvailablePlans from '../../components/AvailablePlans';
import CollapsibleCurrentPlan from '../../components/CollapsibleCurrentPlan';
import CurrentPlan from '../../components/CurrentPlan';
import ErrorWithRefresh from '../../components/Errors/ErrorWithRefresh';
import NoPlansAvailable from '../../components/Errors/NoPlansAvailable';
import PlanErrorContent from '../../components/Errors/PlanErrorContent';
import useElementHeight from '../../hooks/use-element-height';

import styles from './styles.module.scss';

type StylesWithCSSVariable = React.CSSProperties & {
  '--collapsible-current-plan-height': string;
  '--navigation-header-height': string;
};

type PlanSelectionProps = {
  navigateToPreviousPage: () => void;
};

const PlanSelection = ({ navigateToPreviousPage }: PlanSelectionProps) => {
  const { t } = useTranslation('ChangePlan');
  const dispatch = useDispatch();

  const { loading: offersLoading, error, hideBroadbandLabels, planOffersData } = useSelector(selectChangePlan);
  const { platform } = useSelector(selectConfig);

  const { loading: priceLoading } = useSelector(selectCurrentPlanPrice);
  const { loading: characteristicsLoading } = useSelector(selectPlanCharacteristics);

  const currentPlanLoading = priceLoading || characteristicsLoading;

  const { isExtraSmall, isSmall, isMedium, isLarge, isExtraLarge } = useScreenResolution();

  const isMobile = isSmall || isExtraSmall;
  const isMobileOrTablet = isMobile || isMedium;
  const isDesktop = isLarge || isExtraLarge;

  const shouldDisplayBroadbandLabel = !(offersLoading || currentPlanLoading);

  const buttonBackground = !isMobile || hideBroadbandLabels ? undefined : 'white';

  const buttonPosition = platform === Platform.Web || !isMobile ? undefined : '85px';

  const navigationHeaderHeight = useElementHeight('[data-cy=mobileNav]');

  const collapsibleCurrentPlanHeight = useElementHeight('[data-cy=collapsible-current-plan]');

  const collapsibleSummaryHeight = useElementHeight('[data-cy=collapsible-current-plan] summary');

  const cssCustomVariables = isMobileOrTablet
    ? ({
        '--collapsible-current-plan-height': collapsibleCurrentPlanHeight
          ? `${collapsibleCurrentPlanHeight}px`
          : undefined,
        '--navigation-header-height': platform === Platform.Web ? `${navigationHeaderHeight}px` : undefined,
        '--collapsible-summary-height': collapsibleSummaryHeight ? `${collapsibleSummaryHeight}px` : undefined,
      } as StylesWithCSSVariable)
    : {};

  const pageHeaderClassName = `${styles['page-header']} ${
    currentPlanLoading && isMobile ? styles['mobile-loading-header'] : ''
  }`;

  const additionalButtonClassName = `${styles['toggle-labels']} ${hideBroadbandLabels ? 'pressed' : ''}`;

  React.useEffect(() => {
    return () => {
      dispatch(resetHideBroadbandLabels());
    };
  }, []);

  const handleBackNavigation = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    navigateToPreviousPage();
  };

  const handleRefreshOnErrorPage = () => {
    dispatch(refetchPlans());
  };

  if (!offersLoading && planOffersData?.length === 0) {
    return <NoPlansAvailable handleGoBack={handleBackNavigation} />;
  }

  if (error) {
    return (
      <ErrorWithRefresh handleRefresh={handleRefreshOnErrorPage} handleGoBack={handleBackNavigation}>
        <PlanErrorContent />
      </ErrorWithRefresh>
    );
  }

  return (
    <Surface variant="secondary" style={{ flex: 1, ...cssCustomVariables }}>
      <CollapsibleCurrentPlan isHidden={!isMobileOrTablet || currentPlanLoading} />
      <HeaderWithBackNavigation
        handleBackNavigation={handleBackNavigation}
        title={t('mainTitle')}
        additionalClassName={pageHeaderClassName}
        goBackLabel={t('goBack')}
        additionalButton={
          shouldDisplayBroadbandLabel && (
            <Button
              buttonSize="small"
              variant="secondary"
              onClick={() => dispatch(toggleBroadbandLabels())}
              className={additionalButtonClassName}
              style={{
                whiteSpace: 'nowrap',
                backgroundColor: buttonBackground,
                bottom: buttonPosition,
              }}
            >
              {hideBroadbandLabels ? t('showLabels') : t('hideLabels')}
            </Button>
          )
        }
      />

      <Surface variant="secondary" pb={{ xs: '24px', md: '48px' }} className={styles['content-wrapper']}>
        <Surface variant="secondary" className={styles['content']}>
          {isDesktop && (
            <div data-cy="current-plan" className={styles['current-plan-wrapper']}>
              <CurrentPlan />
            </div>
          )}
          <AvailablePlans />
        </Surface>
      </Surface>
    </Surface>
  );
};

export default PlanSelection;
